import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { ViewportScroller, NgFor, NgClass, NgIf, DecimalPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faStar as farStar } from "@fortawesome/free-regular-svg-icons";
import { faStar, faStarHalfAlt } from "@fortawesome/free-solid-svg-icons";
import { notNullOrUndefined } from '../../../common/utils/not-null-or-undefined';

export type FontAwesomeSize = 'xs' | 'sm' | 'lg' | '2x' | '3x' | '4x' | '5x' | '7x' | '10x';
@Component({
    selector: 'kb-star-rating',
    templateUrl: './star-rating.component.html',
    styleUrls: ['./star-rating.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgFor,
        FontAwesomeModule,
        NgClass,
        NgIf,
        RouterLink,
        DecimalPipe,
    ],
})
export class StarRatingComponent {
    @Input() centered = false;
    @Input() rating: number;
    @Input() size: FontAwesomeSize = 'xs';
    @Input() reviewCount: number | null = null;
    @Input() reviewCountRouterLink: any[] | null = null;
    @Input() reviewCountAnchor: string | null = null;
    @Input() interactive = false;
    @Output() starClick = new EventEmitter<number>();
    /**
     * Display mode for the star rating.
     * - 'auto': Display if rating or review count is available and > 0.
     * - 'input': Always display the rating, since it's an input.
     */
    @Input() mode: 'auto' | 'input' = 'auto';
    hoverValue = -1;

    constructor(private viewportScroller: ViewportScroller) {}

    /**
     * Check if the component can display the rating.
     * 1. If the mode is 'input', it should always be visible.
     * 2. If the mode is 'auto' and rating or reviewCount is not null or undefined, it should be visible.
     */
    get canDisplay(): boolean {
        return this.mode === 'input'
            || (notNullOrUndefined(this.reviewCount) && this.reviewCount > 0)
            || (notNullOrUndefined(this.rating) && this.rating > 0);
    }

    get stars(): any[] {
        const rating = this.rating;
        const hovering = this.hoverValue;
        return Array.from({ length: 5 }).map((_, i) => {
            const pos = i + 1;
            const filled = rating >= pos || hovering >= i;
            if (filled) {
                return faStar;
            }
            if (Math.ceil(rating) < pos) {
                return farStar;
            }
            return faStarHalfAlt;
        });
    }

    trackByFn(index: number) {
        return index;
    }

    hoverStar(i: number) {
        if (this.interactive) {
            this.hoverValue = i;
        }
    }

    @HostListener('mouseleave')
    unhover() {
        this.hoverValue = -1;
    }

    starClicked(i: number) {
        if (this.interactive) {
            this.starClick.emit(i + 1);
        }
    }

    scrollToReviewsAnchor(anchor: string) {
        this.viewportScroller.scrollToAnchor(anchor);
    }
}
